export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

let sessionVerification: Promise<boolean | null> | null = null;

function isAuthMeRequest(input: RequestInfo | URL) {
  const value = typeof input === 'string' ? input : input instanceof URL ? input.pathname : input.url;
  return value.includes('/api/me');
}

function redirectToLogin() {
  if (typeof window === 'undefined' || window.location.pathname.startsWith('/login')) return;
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login/?next=${encodeURIComponent(next)}`);
}

async function verifySession(): Promise<boolean | null> {
  if (sessionVerification) return sessionVerification;
  sessionVerification = (async () => {
    try {
      const response = await fetch('/api/me', { credentials: 'same-origin', cache: 'no-store' });
      if (response.ok) return true;
      if (response.status === 401) return false;
      return null;
    } catch {
      return null;
    }
  })().finally(() => {
    sessionVerification = null;
  });
  return sessionVerification;
}

async function requestOnce(input: RequestInfo | URL, init: RequestInit) {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(input, { ...init, headers, credentials: 'same-origin' });
  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json().catch(() => null) : await response.text().catch(() => '');
  return { response, payload };
}

function apiMessage(response: Response, payload: unknown) {
  if (payload && typeof payload === 'object') {
    const body = payload as { message?: unknown; error?: unknown };
    const detailed = typeof body.message === 'string' ? body.message.trim() : '';
    const fallback = typeof body.error === 'string' ? body.error.trim() : '';
    if (detailed) return detailed;
    if (fallback) return fallback;
  }
  return response.statusText || `Request failed (${response.status})`;
}

export async function apiRequest<T>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  let attempt = await requestOnce(input, init);
  if (attempt.response.status === 401 && typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    if (isAuthMeRequest(input)) {
      redirectToLogin();
    } else {
      const verified = await verifySession();
      if (verified === false) {
        redirectToLogin();
      } else if (verified === true) {
        const method = String(init.method ?? 'GET').toUpperCase();
        if ((method === 'GET' || method === 'HEAD') && !init.signal?.aborted) {
          attempt = await requestOnce(input, init);
        }
      }
    }
  }
  if (!attempt.response.ok) throw new ApiError(attempt.response.status, apiMessage(attempt.response, attempt.payload), attempt.payload);
  return attempt.payload as T;
}

export const api = apiRequest;
